import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import TopBar from "@/components/TopBar";
import LocationPicker from "@/components/LocationPicker";
import CustomDatePicker from "@/components/DateTimePicker";
import api, { formatApiError } from "@/lib/api";
import { useConfirm } from "@/components/ConfirmDialog";
import { fmtNum, fmtRiyal } from "@/utils/toEnglishDigits";
import NumberField from "@/components/NumberField";
import { Plus, Pencil, Trash, MagnifyingGlass, Stack, ArrowLeft, TagChevron, Calendar, Tag as TagIcon, FolderOpen, ArrowRight, Warning } from "@phosphor-icons/react";

const emptyRule = () => ({ pickup_pattern: "", destination_pattern: "", car_type: "", fixed_price: 0, note: "" });

function RuleModal({ open, initial, cars, templateId, onClose, onSaved }) {
  const [form, setForm] = useState(emptyRule());
  const [saving, setSaving] = useState(false);
  const isEdit = !!initial?.id;

  useEffect(() => {
    if (open) setForm(initial ? { ...emptyRule(), ...initial } : emptyRule());
  }, [open, initial]);

  if (!open) return null;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.pickup_pattern.trim() || !form.destination_pattern.trim())
      return toast.error("الانطلاق والوجهة مطلوبان");
    if (!form.car_type) return toast.error("نوع السيارة مطلوب");
    if (!form.fixed_price || Number(form.fixed_price) <= 0)
      return toast.error("السعر مطلوب");
    setSaving(true);
    try {
      const payload = { ...form, template_id: templateId || null };
      if (isEdit) {
        await api.patch(`/route-rules/${initial.id}`, payload);
        toast.success("تم تحديث القاعدة");
      } else {
        await api.post("/route-rules", payload);
        toast.success("تم إنشاء القاعدة");
      }
      onSaved?.();
      onClose();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mz-modal-backdrop" onClick={onClose} dir="rtl" data-testid="rule-modal">
      <div className="mz-modal-panel" onClick={(e) => e.stopPropagation()}>
        <div className="mz-modal-header">
          <h3 className="text-primary font-bold text-lg">{isEdit ? "تعديل قاعدة سعر" : "قاعدة سعر جديدة"}</h3>
          <button onClick={onClose} className="text-white/50 hover:text-white text-2xl leading-none" data-testid="rule-modal-close">×</button>
        </div>
        <form onSubmit={submit} className="contents">
          <div className="mz-modal-body">
            <div className="mz-modal-grid">
              {/* Route pickers — unified LocationPicker (chips + clean text input, no examples) */}
              <LocationPicker
                label="نقطة الانطلاق *"
                value={form.pickup_pattern}
                onChange={(v) => set("pickup_pattern", v)}
                includeShrines={false}
                otherEndpointValue={form.destination_pattern}
                testIdPrefix="rule-pickup"
              />
              <LocationPicker
                label="الوجهة *"
                value={form.destination_pattern}
                onChange={(v) => set("destination_pattern", v)}
                includeShrines={true}
                pickupContext={form.pickup_pattern}
                otherEndpointValue={form.pickup_pattern}
                testIdPrefix="rule-destination"
              />

              <div>
                <div className="text-xs text-white/60 mb-1 text-right">نوع السيارة *</div>
                <div dir="rtl" className="flex flex-wrap gap-2" data-testid="rule-car-chips">
                  {cars.map((c) => {
                    const active = form.car_type === c.id;
                    return (
                      <button
                        key={c.id}
                        type="button"
                        onClick={() => set("car_type", c.id)}
                        data-testid={`rule-car-chip-${c.id}`}
                        className={`px-3 py-1.5 rounded-full text-xs border transition-all ${
                          active
                            ? "bg-primary text-primary-foreground border-primary font-bold"
                            : "bg-primary/5 border-primary/30 text-white hover:bg-primary/15"
                        }`}
                      >
                        {c.name_ar}
                      </button>
                    );
                  })}
                </div>
              </div>
              <label className="block">
                <div className="text-xs text-white/60 mb-1 text-right">السعر (ر.س) *</div>
                <NumberField required min={0} value={form.fixed_price} onChange={(val) => set("fixed_price", val === "" ? "" : Number(val))} className="mz-input" testId="rule-price" placeholder="0" />
              </label>

              <label className="block mz-modal-grid-full">
                <div className="text-xs text-white/60 mb-1 text-right">ملاحظة</div>
                <input value={form.note || ""} onChange={(e) => set("note", e.target.value)} className="mz-input" data-testid="rule-note" />
              </label>
            </div>
          </div>
          <div className="mz-modal-footer">
            <button type="submit" disabled={saving} className="flex-1 py-3 rounded-xl bg-primary hover:bg-[#E1C484] text-primary-foreground font-bold disabled:opacity-60" data-testid="rule-save">
              {saving ? "جارٍ الحفظ…" : (isEdit ? "حفظ" : "إنشاء")}
            </button>
            <button type="button" onClick={onClose} className="px-6 py-3 rounded-xl border border-white/20 text-white/70 hover:bg-white/5">إلغاء</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function BulkModal({ open, cars, templateId, onClose, onDone }) {
  const [carType, setCarType] = useState("");
  const [amount, setAmount] = useState(0);
  const [mode, setMode] = useState("set");
  const [busy, setBusy] = useState(false);

  if (!open) return null;

  const apply = async () => {
    if (!carType) return toast.error("اختر نوع السيارة");
    if (!amount || Number(amount) <= 0) return toast.error("أدخل قيمة");
    setBusy(true);
    try {
      const params = templateId ? { template_id: templateId } : {};
      const { data: rules } = await api.get("/route-rules", { params });
      const target = rules.filter((r) => r.car_type === carType);
      if (target.length === 0) { toast.error("لا توجد قواعد لهذا النوع"); return; }
      for (const r of target) {
        const p = Number(r.fixed_price) || 0;
        let np;
        if (mode === "set") np = Number(amount);
        else if (mode === "inc") np = p + Number(amount);
        else if (mode === "pct") np = p * (1 + Number(amount) / 100);
        else np = p;
        await api.patch(`/route-rules/${r.id}`, { fixed_price: Math.round(np), template_id: templateId || null });
      }
      toast.success(`تم تحديث ${fmtNum(target.length)} قاعدة`);
      onDone?.();
      onClose();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(5,10,21,0.85)", backdropFilter: "blur(8px)" }}
      onClick={onClose}
      dir="rtl"
      data-testid="bulk-modal"
    >
      <div className="bg-card border border-primary/25 rounded-2xl w-full max-w-md gold-glow p-5" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-primary font-bold text-lg mb-4">تسعير جماعي</h3>
        <div className="space-y-3">
          <div>
            <div className="text-xs text-white/60 mb-1 text-right">نوع السيارة</div>
            <div dir="rtl" className="flex flex-wrap gap-2" data-testid="bulk-car-chips">
              {cars.map((c) => {
                const active = carType === c.id;
                return (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => setCarType(c.id)}
                    data-testid={`bulk-car-chip-${c.id}`}
                    className={`px-3 py-1.5 rounded-full text-xs border transition-all ${
                      active
                        ? "bg-primary text-primary-foreground border-primary font-bold"
                        : "bg-primary/5 border-primary/30 text-white hover:bg-primary/15"
                    }`}
                  >
                    {c.name_ar}
                  </button>
                );
              })}
            </div>
          </div>
          <label className="block">
            <div className="text-xs text-white/60 mb-1 text-right">نوع العملية</div>
            <select value={mode} onChange={(e) => setMode(e.target.value)} className="mz-input" data-testid="bulk-mode">
              <option value="set">تعيين قيمة ثابتة</option>
              <option value="inc">إضافة مبلغ (+)</option>
              <option value="pct">زيادة نسبة (%)</option>
            </select>
          </label>
          <label className="block">
            <div className="text-xs text-white/60 mb-1 text-right">القيمة</div>
            <NumberField value={amount} onChange={setAmount} className="mz-input" testId="bulk-amount" placeholder="0" />
          </label>
        </div>
        <div className="flex gap-3 mt-5">
          <button onClick={apply} disabled={busy} className="flex-1 py-3 rounded-xl bg-primary hover:bg-[#E1C484] text-primary-foreground font-bold disabled:opacity-60" data-testid="bulk-apply">
            {busy ? "جارٍ التطبيق…" : "تطبيق"}
          </button>
          <button onClick={onClose} className="px-6 py-3 rounded-xl border border-white/20 text-white/70">إلغاء</button>
        </div>
      </div>
    </div>
  );
}

function OverrideModal({ open, initial, cars, onClose, onSaved }) {
  const [form, setForm] = useState({ date_from: "", date_to: "", car_type: "", pickup_pattern: "", destination_pattern: "", fixed_price: 0, reason: "" });
  const [saving, setSaving] = useState(false);
  const isEdit = !!initial?.id;

  useEffect(() => {
    if (open) {
      setForm(initial ? {
        date_from: (initial.date_from || "").slice(0, 10),
        date_to: (initial.date_to || "").slice(0, 10),
        car_type: initial.car_type || "",
        pickup_pattern: initial.pickup_pattern || "",
        destination_pattern: initial.destination_pattern || "",
        fixed_price: initial.fixed_price || 0,
        reason: initial.reason || "",
      } : { date_from: "", date_to: "", car_type: "", pickup_pattern: "", destination_pattern: "", fixed_price: 0, reason: "" });
    }
  }, [open, initial]);

  if (!open) return null;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.date_from || !form.date_to) return toast.error("من/إلى تاريخ مطلوبان");
    if (!form.car_type) return toast.error("نوع السيارة مطلوب");
    if (!form.fixed_price || Number(form.fixed_price) <= 0) return toast.error("السعر مطلوب");
    setSaving(true);
    try {
      if (isEdit) {
        await api.patch(`/date-overrides/${initial.id}`, form);
        toast.success("تم تحديث التجاوز");
      } else {
        await api.post("/date-overrides", form);
        toast.success("تم إنشاء التجاوز");
      }
      onSaved?.();
      onClose();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mz-modal-backdrop" onClick={onClose} dir="rtl" data-testid="override-modal">
      <div className="mz-modal-panel" onClick={(e) => e.stopPropagation()}>
        <div className="mz-modal-header">
          <h3 className="text-primary font-bold text-lg">{isEdit ? "تعديل تجاوز تاريخ" : "تجاوز تاريخ جديد"}</h3>
          <button onClick={onClose} className="text-white/50 hover:text-white text-2xl leading-none">×</button>
        </div>
        <form onSubmit={submit} className="contents">
          <div className="mz-modal-body">
            <div className="mz-modal-grid">
              <div>
                <div className="text-xs text-white/60 mb-1 text-right">من تاريخ *</div>
                <CustomDatePicker
                  value={form.date_from}
                  onChange={(v) => set("date_from", v)}
                  placeholder="YYYY-MM-DD"
                  testId="override-from"
                />
              </div>
              <div>
                <div className="text-xs text-white/60 mb-1 text-right">إلى تاريخ *</div>
                <CustomDatePicker
                  value={form.date_to}
                  onChange={(v) => set("date_to", v)}
                  placeholder="YYYY-MM-DD"
                  testId="override-to"
                />
              </div>

              {/* Unified LocationPicker — optional pickup/destination (empty = applies to all) */}
              <LocationPicker
                label="نقطة الانطلاق (اختياري — اترك فارغاً للتطبيق على الكل)"
                value={form.pickup_pattern}
                onChange={(v) => set("pickup_pattern", v)}
                includeShrines={false}
                otherEndpointValue={form.destination_pattern}
                testIdPrefix="override-pickup"
              />
              <LocationPicker
                label="الوجهة (اختياري)"
                value={form.destination_pattern}
                onChange={(v) => set("destination_pattern", v)}
                includeShrines={true}
                pickupContext={form.pickup_pattern}
                otherEndpointValue={form.pickup_pattern}
                testIdPrefix="override-destination"
              />

              <div>
                <div className="text-xs text-white/60 mb-1 text-right">نوع السيارة *</div>
                <div dir="rtl" className="flex flex-wrap gap-2" data-testid="override-car-chips">
                  {cars.map((c) => {
                    const active = form.car_type === c.id;
                    return (
                      <button
                        key={c.id}
                        type="button"
                        onClick={() => set("car_type", c.id)}
                        data-testid={`override-car-chip-${c.id}`}
                        className={`px-3 py-1.5 rounded-full text-xs border transition-all ${
                          active
                            ? "bg-primary text-primary-foreground border-primary font-bold"
                            : "bg-primary/5 border-primary/30 text-white hover:bg-primary/15"
                        }`}
                      >
                        {c.name_ar}
                      </button>
                    );
                  })}
                </div>
              </div>
              <label className="block">
                <div className="text-xs text-white/60 mb-1 text-right">السعر الجديد (ر.س) *</div>
                <NumberField required min={0} value={form.fixed_price} onChange={(val) => set("fixed_price", val === "" ? "" : Number(val))} className="mz-input" testId="override-price" placeholder="0" />
              </label>

              <label className="block mz-modal-grid-full">
                <div className="text-xs text-white/60 mb-1 text-right">السبب / الموسم</div>
                <input value={form.reason} onChange={(e) => set("reason", e.target.value)} className="mz-input" data-testid="override-reason" />
              </label>
            </div>
          </div>
          <div className="mz-modal-footer">
            <button type="submit" disabled={saving} className="flex-1 py-3 rounded-xl bg-primary hover:bg-[#E1C484] text-primary-foreground font-bold disabled:opacity-60" data-testid="override-save">
              {saving ? "جارٍ الحفظ…" : (isEdit ? "حفظ" : "إنشاء")}
            </button>
            <button type="button" onClick={onClose} className="px-6 py-3 rounded-xl border border-white/20 text-white/70 hover:bg-white/5">إلغاء</button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function RoutePricing() {
  const [searchParams] = useSearchParams();
  const templateId = searchParams.get("template_id") || "";
  const templateName = searchParams.get("template_name") || "";
  const isTemplateScope = !!templateId;

  const [tab, setTab] = useState("rules");
  const [rules, setRules] = useState([]);
  const [cars, setCars] = useState([]);
  const [overrides, setOverrides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [carF, setCarF] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [overrideModalOpen, setOverrideModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editingOverride, setEditingOverride] = useState(null);
  const confirm = useConfirm();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const rulesParams = templateId ? { template_id: templateId } : {};
      const [r, c, o] = await Promise.all([
        api.get("/route-rules", { params: rulesParams }),
        api.get("/cars"),
        api.get("/date-overrides").catch(() => ({ data: [] })),
      ]);
      setRules(r.data);
      setCars(c.data);
      setOverrides(o.data || []);
    } catch {
      toast.error("تعذر التحميل");
    } finally {
      setLoading(false);
    }
  }, [templateId]);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    let list = rules;
    if (carF) list = list.filter((r) => r.car_type === carF);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter((r) =>
        (r.pickup_pattern || "").toLowerCase().includes(q) ||
        (r.destination_pattern || "").toLowerCase().includes(q)
      );
    }
    return list;
  }, [rules, carF, search]);

  const carName = (id) => cars.find((c) => c.id === id)?.name_ar || "—";

  const remove = async (r) => {
    const ok = await confirm({
      title: "حذف قاعدة السعر",
      message: `سيتم حذف القاعدة:\n${r.pickup_pattern} ← ${r.destination_pattern}\nهل أنت متأكد؟`,
      confirmText: "نعم، احذف",
      tone: "danger",
    });
    if (!ok) return;
    try {
      await api.delete(`/route-rules/${r.id}`);
      toast.success("تم الحذف");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const removeOverride = async (o) => {
    const ok = await confirm({
      title: "حذف تجاوز التاريخ",
      message: `سيتم حذف تجاوز الفترة (${o.date_from} — ${o.date_to}).`,
      confirmText: "نعم، احذف",
      tone: "danger",
    });
    if (!ok) return;
    try {
      await api.delete(`/date-overrides/${o.id}`);
      toast.success("تم الحذف");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground" dir="rtl">
      <TopBar />
      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* Contextual banner — Gold when scoped to a template, Blue for general rules */}
        <div
          className={`mb-5 rounded-2xl border-2 p-4 flex items-center justify-between flex-wrap gap-3 ${
            isTemplateScope
              ? "border-primary/60 bg-gradient-to-l from-primary/15 to-primary/5"
              : "border-[#3B82F6]/40 bg-gradient-to-l from-[#3B82F6]/10 to-[#0B1A3A]"
          }`}
          data-testid={isTemplateScope ? "rp-banner-template" : "rp-banner-general"}
        >
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <div
              className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${
                isTemplateScope
                  ? "bg-primary/20 border border-primary/50 text-primary"
                  : "bg-[#3B82F6]/20 border border-[#3B82F6]/50 text-[#3B82F6]"
              }`}
            >
              {isTemplateScope ? (
                <FolderOpen size={22} weight="fill" />
              ) : (
                <TagIcon size={22} weight="fill" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-white font-bold text-base flex items-center gap-2 flex-wrap">
                {isTemplateScope ? (
                  <>
                    <span>قالب:</span>
                    <span
                      className="text-primary truncate max-w-[240px]"
                      data-testid="rp-template-name"
                    >
                      {templateName || "—"}
                    </span>
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-primary/20 border border-primary/50 text-primary font-bold">
                      حصري
                    </span>
                  </>
                ) : (
                  <>
                    <span>الأسعار العامة</span>
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#3B82F6]/20 border border-[#3B82F6]/50 text-[#3B82F6] font-bold">
                      افتراضي
                    </span>
                  </>
                )}
              </div>
              <div className="text-white/60 text-xs mt-0.5">
                {isTemplateScope
                  ? "قواعد أسعار حصرية لهذا القالب — تُستخدم فقط للشركات المرتبطة به."
                  : "قواعد الأسعار الافتراضية للشركات غير المرتبطة بأي قالب."}
              </div>
            </div>
          </div>
          <Link
            to="/admin/pricing-templates"
            data-testid="rp-back-to-templates"
            className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold transition-all ${
              isTemplateScope
                ? "border border-primary/50 text-primary hover:bg-primary/10"
                : "border border-[#3B82F6]/50 text-[#3B82F6] hover:bg-[#3B82F6]/10"
            }`}
          >
            <FolderOpen size={14} weight="duotone" /> إدارة القوالب
            <ArrowRight size={12} weight="bold" className="rotate-180" />
          </Link>
        </div>

        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-white">إدارة الأسعار</h1>
            <p className="text-white/50 text-sm mt-1">
              <span className="num">{fmtNum(rules.length)}</span> قاعدة سعر · <span className="num">{fmtNum(overrides.length)}</span> تجاوز موسمي
            </p>
          </div>
          {tab === "rules" ? (
            <div className="flex items-center gap-2 flex-wrap">
              {/* 2026-07-28 §ImportGeneral: quick way to bootstrap a template
                  by copying all missing general rules into it. Only shown when
                  we are scoped to a specific template. */}
              {isTemplateScope && (
                <button
                  onClick={async () => {
                    const ok = await confirm({
                      title: "استيراد الأسعار العامة إلى القالب",
                      message:
                        "سيتم نسخ كل قواعد الأسعار العامة (~105 قاعدة) إلى هذا القالب. القواعد الموجودة مسبقاً لن تُحدَّث — فقط المفقودة تُضاف. يمكنك بعدها تعديل الأسعار كما تريد.\n\nتابع؟",
                      confirmText: "نعم، استورد",
                      tone: "primary",
                    });
                    if (!ok) return;
                    try {
                      const { data } = await api.post(
                        `/admin/pricing-templates/${templateId}/import-general`,
                        { mode: "missing" },
                      );
                      toast.success(
                        `تم استيراد ${data.inserted} قاعدة جديدة (تخطينا ${data.skipped} موجودة).`,
                      );
                      load();
                    } catch (err) {
                      toast.error(formatApiError(err));
                    }
                  }}
                  data-testid="import-general-rules"
                  className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#F57F17]/20 border border-[#F57F17]/50 text-[#F57F17] rounded-xl hover:bg-[#F57F17]/30 font-bold"
                  title="نسخ الأسعار العامة إلى هذا القالب لتعبئته سريعاً"
                >
                  <ArrowLeft size={18} weight="bold" className="rotate-90" /> استيراد الأسعار العامة
                </button>
              )}
              <button onClick={() => setBulkOpen(true)} data-testid="bulk-pricing" className="inline-flex items-center gap-2 px-4 py-2.5 border border-primary/40 text-primary rounded-xl hover:bg-primary/10 font-bold">
                <Stack size={18} weight="bold" /> تسعير جماعي
              </button>
              <button onClick={() => { setEditing(null); setModalOpen(true); }} data-testid="add-rule" className="inline-flex items-center gap-2 px-4 py-2.5 bg-primary hover:bg-[#E1C484] text-primary-foreground rounded-xl font-bold">
                <Plus size={18} weight="bold" /> قاعدة جديدة
              </button>
            </div>
          ) : (
            <button onClick={() => { setEditingOverride(null); setOverrideModalOpen(true); }} data-testid="add-override" className="inline-flex items-center gap-2 px-4 py-2.5 bg-primary hover:bg-[#E1C484] text-primary-foreground rounded-xl font-bold">
              <Plus size={18} weight="bold" /> تجاوز تاريخ جديد
            </button>
          )}
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-5">
          <button
            onClick={() => setTab("rules")}
            data-testid="tab-rules"
            className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold transition-all ${
              tab === "rules" ? "bg-primary text-primary-foreground" : "border border-primary/25 text-white/70 hover:border-primary/50 hover:text-primary"
            }`}
          >
            <TagIcon size={16} weight={tab === "rules" ? "fill" : "duotone"} /> أسعار المسارات
          </button>
          <button
            onClick={() => setTab("overrides")}
            data-testid="tab-overrides"
            className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold transition-all ${
              tab === "overrides" ? "bg-primary text-primary-foreground" : "border border-primary/25 text-white/70 hover:border-primary/50 hover:text-primary"
            }`}
          >
            <Calendar size={16} weight={tab === "overrides" ? "fill" : "duotone"} /> تجاوزات التاريخ
          </button>
        </div>

        {/* Car type chips — visible for BOTH tabs, filters the table below */}
        <div className="mb-5">
          <div className="text-primary font-bold text-sm mb-2">🚗 فلترة حسب نوع السيارة:</div>
          <div dir="rtl" className="flex flex-row items-center flex-wrap gap-2" data-testid="rp-car-chips">
            {[{ id: "", name_ar: "الكل" }, ...cars].map((c) => {
              const active = carF === c.id;
              const count = c.id
                ? (tab === "rules"
                    ? rules.filter((r) => r.car_type === c.id).length
                    : overrides.filter((o) => o.car_type === c.id).length)
                : (tab === "rules" ? rules.length : overrides.length);
              return (
                <button
                  key={c.id || "all"}
                  type="button"
                  onClick={() => setCarF(c.id)}
                  data-testid={`rp-chip-car-${c.id || "all"}`}
                  className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm border transition-all whitespace-nowrap ${
                    active
                      ? "bg-primary text-primary-foreground border-primary font-bold"
                      : "bg-primary/5 border-primary/30 text-white hover:bg-primary/15 font-semibold"
                  }`}
                >
                  <span>{c.name_ar}</span>
                  <span
                    className={`num text-[11px] font-bold px-2 py-0.5 rounded-full ${
                      active ? "bg-[#0A1936]/25 text-primary-foreground" : "bg-white/10 text-white"
                    }`}
                  >
                    {fmtNum(count)}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {tab === "rules" ? (
          <>
            {/* Search */}
            <div className="mb-5">
              <div className="relative max-w-xl">
                <MagnifyingGlass size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-primary/60" />
                <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="بحث بالانطلاق أو الوجهة…" className="mz-input pr-9" data-testid="rules-search" />
              </div>
            </div>

        {loading ? (
          <div className="text-center py-16 text-primary animate-pulse">جارٍ التحميل…</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 text-white/50 bg-card border border-primary/15 rounded-2xl">لا توجد قواعد أسعار</div>
        ) : (
          <div className="bg-card border border-primary/15 rounded-2xl overflow-hidden">
            <div className="hidden md:grid grid-cols-[1fr_1fr_180px_140px_120px] gap-2 bg-[#0B1A3A] p-3 text-primary/80 text-xs font-bold">
              <div>الانطلاق</div>
              <div>الوجهة</div>
              <div>السيارة</div>
              <div>السعر</div>
              <div className="text-center">إجراءات</div>
            </div>
            {filtered.map((r, i) => (
              <div
                key={r.id}
                data-testid={`rule-${r.id}`}
                className="fadeup border-t border-primary/10 hover:bg-primary/5 transition-all"
                style={{ animationDelay: `${Math.min(i, 12) * 20}ms` }}
              >
                <div className="hidden md:grid grid-cols-[1fr_1fr_180px_140px_120px] gap-2 items-center p-3 text-sm">
                  <div className="text-white flex items-center gap-1.5"><TagChevron size={14} className="text-primary/60" />{r.pickup_pattern}</div>
                  <div className="text-white/80">{r.destination_pattern}</div>
                  <div className="text-white/70 text-xs">{carName(r.car_type)}</div>
                  <div className="text-primary font-bold num">{fmtRiyal(r.fixed_price)}</div>
                  <div className="flex items-center gap-2 justify-center">
                    <button onClick={() => { setEditing(r); setModalOpen(true); }} data-testid={`edit-rule-${r.id}`} className="p-2 rounded-lg border border-primary/30 text-primary hover:bg-primary/10">
                      <Pencil size={13} weight="bold" />
                    </button>
                    <button onClick={() => remove(r)} data-testid={`delete-rule-${r.id}`} className="p-2 rounded-lg border border-destructive/50 text-destructive hover:bg-destructive/10">
                      <Trash size={13} weight="bold" />
                    </button>
                  </div>
                </div>
                {/* Mobile */}
                <div className="md:hidden p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-primary font-bold num">{fmtRiyal(r.fixed_price)}</div>
                    <div className="text-white/50 text-xs">{carName(r.car_type)}</div>
                  </div>
                  <div className="text-white text-sm">{r.pickup_pattern} <ArrowLeft size={12} className="inline text-primary/50 mx-1" /> {r.destination_pattern}</div>
                  <div className="flex items-center gap-2 mt-3">
                    <button onClick={() => { setEditing(r); setModalOpen(true); }} className="flex-1 py-2 rounded-lg border border-primary/30 text-primary hover:bg-primary/10 text-xs">تعديل</button>
                    <button onClick={() => remove(r)} className="flex-1 py-2 rounded-lg border border-destructive/50 text-destructive hover:bg-destructive/10 text-xs">حذف</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
          </>
        ) : (
          /* ============ Date Overrides Tab ============ */
          <div>
            {(() => {
              const filteredOverrides = carF ? overrides.filter((o) => o.car_type === carF) : overrides;
              if (loading) {
                return <div className="text-center py-16 text-primary animate-pulse">جارٍ التحميل…</div>;
              }
              if (filteredOverrides.length === 0) {
                return (
                  <div className="text-center py-16 text-white/50 bg-card border border-primary/15 rounded-2xl" data-testid="overrides-empty">
                    <Calendar size={40} weight="duotone" className="text-primary/40 mx-auto mb-2" />
                    {carF
                      ? "لا توجد تجاوزات لهذا النوع من السيارات."
                      : "لا توجد تجاوزات موسمية بعد. أضف واحداً لتحديد أسعار خاصة للمواسم."}
                  </div>
                );
              }
              return (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="overrides-list">
                {filteredOverrides.map((o, i) => (
                  <div
                    key={o.id}
                    data-testid={`override-${o.id}`}
                    className="fadeup bg-card border border-primary/15 rounded-2xl p-5 hover:border-primary/40 transition-all"
                    style={{ animationDelay: `${Math.min(i, 12) * 40}ms` }}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="w-11 h-11 rounded-xl bg-primary/10 border border-primary/30 flex items-center justify-center">
                        <Calendar size={20} weight="duotone" className="text-primary" />
                      </div>
                      <div className="text-primary font-bold num text-lg">{fmtRiyal(o.fixed_price)}</div>
                    </div>
                    <div className="text-white text-sm num" dir="ltr">
                      {(o.date_from || "").slice(0, 10)} → {(o.date_to || "").slice(0, 10)}
                    </div>
                    <div className="text-white/70 text-xs mt-2">
                      {carName(o.car_type)}
                      {o.pickup_pattern && <><span className="mx-1">·</span>{o.pickup_pattern} ← {o.destination_pattern || "الكل"}</>}
                    </div>
                    {o.reason && (
                      <div className="mt-2 text-primary/80 text-xs italic">&quot;{o.reason}&quot;</div>
                    )}
                    <div className="flex items-center gap-2 mt-4 pt-3 border-t border-primary/10">
                      <button
                        onClick={() => { setEditingOverride(o); setOverrideModalOpen(true); }}
                        data-testid={`edit-override-${o.id}`}
                        className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg border border-primary/30 text-primary hover:bg-primary/10 text-xs font-bold"
                      >
                        <Pencil size={13} weight="bold" /> تعديل
                      </button>
                      <button
                        onClick={() => removeOverride(o)}
                        data-testid={`delete-override-${o.id}`}
                        className="p-2 rounded-lg border border-destructive/50 text-destructive hover:bg-destructive/10"
                      >
                        <Trash size={13} weight="bold" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
              );
            })()}
          </div>
        )}
      </main>

      <RuleModal open={modalOpen} initial={editing} cars={cars} templateId={templateId} onClose={() => setModalOpen(false)} onSaved={load} />
      <BulkModal open={bulkOpen} cars={cars} templateId={templateId} onClose={() => setBulkOpen(false)} onDone={load} />
      <OverrideModal open={overrideModalOpen} initial={editingOverride} cars={cars} onClose={() => setOverrideModalOpen(false)} onSaved={load} />
    </div>
  );
}
